import {
  QuestionThirteenForm,
  SurveyResponse,
  VWCarModel,
} from './../types/surveyResponse';
import {
  generateCarModelName,
  generateCarModelNameFromId,
  generateSurveyUrl,
} from './resultSections';
import {Lead, SurveyAnswers, TradeInEvaluation} from '../types/salesforce';
import {SalesforceSurvey} from '../questions/schema';
import {QuestionData} from '../constants/questions';
import {QuestionId} from '../store/navigation';
import _ from 'lodash';

type State = 'NSW' | 'ACT' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'NT';

const getPostcodeState = (postcode?: string): State | null => {
  if (!postcode) return null;
  const firstNumber = postcode[0];
  const postcodeByFirstNumber: {[key: string]: State | null} = {
    '2': 'NSW',
    '3': 'VIC',
    '4': 'QLD',
    '5': 'SA',
    '6': 'WA',
    '7': 'TAS',
    '0': 'NT',
  };

  return postcodeByFirstNumber[firstNumber] ?? null;
};

export const formatLead = (
  questionThirteenForm: QuestionThirteenForm,
  surveyResponse: SurveyResponse,
): Lead => {
  if (!questionThirteenForm || !surveyResponse) return {} as Lead;

  let [model1, model2] = generateCarModelName(surveyResponse).split('And');

  // Get model name by id if they separated by 'And'
  if (model1 && model2) {
    model1 = generateCarModelNameFromId(model1 as VWCarModel);
    model2 = generateCarModelNameFromId(model2 as VWCarModel);
  }
  return {
    first_name: questionThirteenForm.firstName ?? '',
    last_name: questionThirteenForm.lastName ?? '',
    email_address: questionThirteenForm.emailAddress ?? '',
    region_code: getPostcodeState(questionThirteenForm.postcode) ?? '',
    post_code: questionThirteenForm.postcode ?? '',
    country_code: 'AU',
    mobile_phone: questionThirteenForm.phone ?? '',
    vehicle_interested_in:
      (model1 || generateCarModelName(surveyResponse)) ?? '',
    tracking_code_did: 1574,
    lead_type: questionThirteenForm.callback
      ? 'Callback Request'
      : questionThirteenForm.subscribe
      ? 'Newsletter Signup'
      : 'Car Finder',
    sub_brand: 'Passenger Vehicles (PV)',
    brand: 'Volkswagen',
    source: 'WEBSITE',
    model_1: model1 || '',
    model_2: model2 || '',
  };
};

const formatSurveyAnswers = (surveyResponse: SurveyResponse): SurveyAnswers => {
  if (!surveyResponse) return {} as SurveyAnswers;

  const survey = _.cloneDeep(surveyResponse);

  let [model1, model2] = generateCarModelName(survey).split('And');

  // Get model name by id if they separated by 'And'
  if (model1 && model2) {
    model1 = generateCarModelNameFromId(model1 as VWCarModel);
    model2 = generateCarModelNameFromId(model2 as VWCarModel);
  }

  // Replace select option value with label instead
  const questionIds = Object.keys(survey);

  console.log('salesForceFormatter_formatSurveyAnswers_88', survey);
  for (const questionId of questionIds) {
    const question = QuestionData[questionId as QuestionId];

    if ('singleSelectOptions' in question) {
      const options = question.singleSelectOptions;

      const valueFromSurveyResponse = survey[questionId as QuestionId];
      if (typeof valueFromSurveyResponse === 'string') {
        const option = options.find(
          (option) => option.value === valueFromSurveyResponse,
        );
        // @ts-expect-error Bypass type check
        survey[questionId as QuestionId] =
          option?.label ?? valueFromSurveyResponse;
      }
    }

    if ('multiSelectOptions' in question) {
      const options = question.multiSelectOptions;

      const valueFromSurveyResponse = survey[questionId as QuestionId];
      if (Array.isArray(valueFromSurveyResponse)) {
        // @ts-expect-error Bypass type check
        survey[questionId as QuestionId] = valueFromSurveyResponse.map(
          (value) => {
            const option = options.find((option) => option.value === value);
            return option?.label ?? value;
          },
        );
      }
    }
  }

  return {
    usage: survey.q3_vehicle_use ?? '',
    no_of_passengers: JSON.stringify(survey.q4_passengers) ?? '',
    towing_vehicle: survey.q5_towing_preference ?? '',
    vehicle_trade: survey.q6_fits_your_trade ?? '',
    reason_for_interest: survey.q7_new_vehicle_features?.join(';') ?? '',
    energy_options: survey.q9_charging_type?.join(';') ?? '',
    charger_installed: survey.q10_charging_home ?? '',
    charger_access: survey.q11_charging_work ?? '',
    distance_travelled: JSON.stringify(survey.q12_travel_distance) ?? '',
    recommended_model_1: model1 || '',
    recommended_model_2: model2 || '',
    survey_url: generateSurveyUrl(surveyResponse, window.location.href),
  };
};

export const formatSalesForceObject = (
  lead: Lead,
  surveyResponse: SurveyResponse,
  tradeInEvaluation: TradeInEvaluation,
): SalesforceSurvey => {
  {
    if (!lead || !surveyResponse || !tradeInEvaluation)
      return {} as SalesforceSurvey;
    return {
      lead: lead as Lead,
      trade_in_evaluation: formatTradeInEvaluation(
        tradeInEvaluation as TradeInEvaluation,
      ),
      survey_answers: formatSurveyAnswers(surveyResponse) as SurveyAnswers,
    };
  }
};

function formatTradeInEvaluation(
  payload: TradeInEvaluation,
): TradeInEvaluation {
  const keysThatNeedToBeTransformToArray: Array<keyof TradeInEvaluation> = [
    'badge2nd',
    'bodyconfig',
    'wheelbaseconfig',
    'roofline',
    'extrainfo',
    'greenhouserating',
    'airpollutionrating',
    'overallgreenstarrating',
    'maxethanolblend',
  ];

  const keysThatRequired: Array<keyof TradeInEvaluation> = [
    'rego_no',
    'state',
    'make',
    'model',
    'variant',
    'redbookcode',
    'rbid',
    'makecode',
    'familycode',
    'vehicletype',
    'year',
    'month',
    'sequenceno',
    'description',
    'newvehicleflag',
    'importflag',
    'limitededition',
    'series',
    'seriesmodelyear',
    'badge',
    'bodystyle',
    'drive',
    'geartype',
    'gearlocation',
    'gearnum',
    'doornum',
    'enginesize',
    'enginedesc',
    'cylinders',
    'fueltype',
    'induction',
    'co2',
    'co2urban',
    'co2extraurban',
    'fuelurban',
    'fuelextraurban',
    'fuelcombined',
    'emissionstandard',
    'vfactsclass',
    'vfactssegment',
    'vfactsprice',
    'ancaprating',
    'avgkm',
    'gdkm',
    'avgwhs',
    'avgretail',
    'goodwhs',
    'goodretail',
    'tradeinmin',
    'tradeinmax',
    'privatemin',
    'privatemax',
  ];

  for (const key of keysThatNeedToBeTransformToArray) {
    const value = payload[key];

    if (typeof value === 'undefined') {
      payload[key] = [];
      continue;
    }

    if (typeof value === 'string' && value.length === 0) {
      payload[key] = [];
      continue;
    }

    if (!Array.isArray(value)) {
      payload[key] = [value?.toString() || ''];
    }
  }

  for (const key of keysThatRequired) {
    const value = payload[key];

    payload[key] = value || '';
  }

  return payload;
}
